import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { FaChartBar, FaChartLine, FaCalendarAlt } from 'react-icons/fa';

Chart.register(...registerables);

const ReportsPage = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '2020-01-01',
    endDate: '2023-12-31'
  });

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('https://backend.stu89674.webhosting.arden.ac.uk/api/reports/sales', {
        params: dateRange
      });
      setReportData(data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchReportData();
  };

  // Sample data for charts (in a real app, this would come from the API)
  const monthlySalesData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Vehicles Sold',
        data: [12, 19, 15, 21, 18, 25, 22, 30, 26, 20, 18, 24],
        backgroundColor: 'rgba(13, 110, 253, 0.5)',
        borderColor: 'rgba(13, 110, 253, 1)',
        borderWidth: 1,
      },
    ],
  };

  const revenueTrendData = {
    labels: ['2019', '2020', '2021', '2022', '2023'],
    datasets: [
      {
        label: 'Annual Revenue ($)',
        data: [125000, 185000, 210000, 245000, 290000],
        fill: false,
        backgroundColor: 'rgba(25, 135, 84, 0.5)',
        borderColor: 'rgba(25, 135, 84, 1)',
        tension: 0.1,
      },
    ],
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Sales Reports</h1>
      
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <Form.Group controlId="startDate">
                  <Form.Label><FaCalendarAlt className="me-2" />Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="endDate">
                  <Form.Label><FaCalendarAlt className="me-2" />End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button variant="primary" type="submit" className="w-100">
                  Generate Report
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : reportData ? (
        <>
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Total Orders</Card.Title>
                  <Card.Text className="display-4 text-primary">
                    {reportData.total_orders}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Total Revenue</Card.Title>
                  <Card.Text className="display-4 text-success">
                    ${reportData.total_revenue?.toLocaleString() || '0'}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Average Order Value</Card.Title>
                  <Card.Text className="display-4 text-info">
                    ${reportData.average_order_value?.toFixed(2) || '0'}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title><FaChartBar className="me-2" />Monthly Sales</Card.Title>
                  <div style={{ height: '300px' }}>
                    <Bar 
                      data={monthlySalesData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true
                          }
                        }
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title><FaChartLine className="me-2" />Revenue Trend</Card.Title>
                  <div style={{ height: '300px' }}>
                    <Line 
                      data={revenueTrendData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true
                          }
                        }
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <Card>
          <Card.Body className="text-center py-5">
            <h4>No report data available</h4>
            <p>Select a date range and generate a report</p>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ReportsPage;